<template>
  <div class="accordion-table">
    <div
      v-if="props.data?.length"
      class="accordion-table-header row"
      :class="{ 'row--first-column-wide': firstColumnWide }"
    >
      <div class="column column--first">
        {{ titles.title1 }}
      </div>
      <div class="column">
        {{ titles.title2 }}
      </div>
      <div v-if="titles.title3" class="column">
        {{ titles.title3 }}
      </div>
      <div class="column"></div>
    </div>
    <div class="accordion-table-container">
      <div v-for="object in props.data" :key="object.id">
        <div
          class="row"
          :class="{ 'row--clickable': clickable, 'row--first-column-wide': firstColumnWide }"
          :role="clickable ? 'button' : undefined"
          :tabindex="clickable ? 0 : -1"
          @click="clickable ? emits('toggleCategory', object.id) : ''"
          @keydown.enter="clickable ? emits('toggleCategory', object.id) : ''"
          @keydown.space="(event: Event) => clickable ? handleSpaceKey(event, object.id) : ''"
        >
          <div
            class="column column--first"
            @mouseleave="showTooltip = false"
            @mouseenter="(e) => trackPointer(e, object.value1 ?? '')"
          >
            <p>{{ object.value1 }}</p>
            <TooltipStandard
              v-show="showTooltip"
              arrowPosition="top-center"
              class="tooltip"
              :pointerEvent="trackedMouseEvent"
              :text="hoveredTitle"
            />
          </div>
          <div class="column">
            {{ object.value2 }}
          </div>
          <div v-if="object.value3" class="column">
            {{ object.value3 }}
          </div>
          <div class="column">
            <IconChevroneUpSmall
              v-if="clickable"
              class="arrow-button-icon"
              :class="{ 'arrow-button-icon--object-expanded': object.visible }"
            />
          </div>
        </div>
        <div v-if="object.visible === true" class="accordion-table-sub-container">
          <div
            v-for="item in object.items"
            :key="item.id"
            class="row"
            :class="{ 'row--first-column-wide': firstColumnWide }"
          >
            <div
              class="column column--first"
              @mouseleave="showTooltip = false"
              @mouseenter="(e) => trackPointer(e, item.value1 ?? '')"
            >
              <p>{{ item.value1 }}</p>
              <span v-if="item.color" class="wrapper-box-circle">
                <SvgDisplay :src="'Icon-Circle-Small'" :style="`color: ${item.color}`"></SvgDisplay>
              </span>
              <TooltipStandard
                v-show="showTooltip"
                arrowPosition="top-center"
                class="tooltip"
                :pointerEvent="trackedMouseEvent"
                :text="hoveredTitle"
              />
            </div>
            <div class="column">
              {{ item.value2 }}
            </div>
            <div v-if="item.value3" class="column">
              {{ item.value3 }}
            </div>
            <div class="column"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import IconChevroneUpSmall from '@/design-system/assets/icons/IconChevroneUpSmall.vue'
import SvgDisplay from '@/design-system/atoms/SvgDisplay.vue'
import TooltipStandard from '@/design-system/atoms/TooltipStandard.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'

export interface OVERVIEW_DATA {
  id: string
  value1: string
  value2: number
  value3?: string | number
  items: any[]
  visible?: boolean
}
const props = defineProps<{
  data: OVERVIEW_DATA[]
  firstColumnWide: boolean
  titles: {
    title1: string
    title2: string
    title3?: string
  }
  clickable: boolean
}>()

const emits = defineEmits(['toggleCategory'])

const store = useStore()

const hoveredTitle = ref<string>('')
const lastMousePosition = ref([0, 0])
const showTooltip = ref(false)
const trackedMouseEvent = ref<object>()
const windowWidth = ref<number>(window.innerWidth)
const mobileScreen = computed(() => {
  return store.state.mobileScreen
})
const gridStyle = computed(() => {
  if (mobileScreen.value && windowWidth.value > 658)
    return props.data.some((object) => object.value3) ? '70% 1fr 1fr 0.5fr' : '80% 1fr 0.5fr'
  return props.data.some((object) => object.value3) ? '40% 1fr 1fr 0.5fr' : '65% 1fr 0.5fr'
})

function handleSpaceKey(event: Event, objectId: string) {
  event.preventDefault() // Prevent space bar scrolling
  emits('toggleCategory', objectId)
}
function setWindowWidth() {
  windowWidth.value = window.innerWidth
}
function trackPointer(event, title: string) {
  hoveredTitle.value = title
  const rect = event.currentTarget.getBoundingClientRect()
  if (event) {
    if (
      lastMousePosition.value[0] === event.clientX &&
      lastMousePosition.value[1] === event.clientY
    )
      return
    lastMousePosition.value = [event.clientX, event.clientY]
    trackedMouseEvent.value = {
      x: rect.left + rect.width / 2,
      y: rect.bottom + rect.height / 2
    }
    const targetEl = event.target as HTMLElement
    if (targetEl.firstElementChild) {
      showTooltip.value =
        targetEl.firstElementChild.clientWidth < targetEl.firstElementChild.scrollWidth
    }
  }
}

onMounted(() => {
  window.addEventListener('resize', setWindowWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', setWindowWidth)
})
</script>
<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/mixins.scss';
@use '@/styles/variables.scss' as old-variables;

.accordion-table {
  @include font-mixins.desktop-regular-paragraph;
  color: variables.$color-greyscale-content;
  position: relative;
  width: 100%;

  p {
    padding: 0;
    margin: 0;
    @include font-mixins.desktop-regular-paragraph;
    color: variables.$color-greyscale-content;
  }
  &-header {
    @include font-mixins.desktop-bold-paragraph;
    align-items: center;
    background-color: rgba(variables.$color-primary-light, 0.4);
    display: flex;
    height: 32px;
    justify-content: space-between;
    padding: 0;
    width: 100%;
    th {
      padding: 4px;

      &:nth-child(2) {
        float: right;
        margin-left: auto;
      }
      &:last-child {
        justify-content: flex-end;
        padding-right: 8px;
      }
      &:not(:first-child) {
        justify-content: flex-end;
      }
    }
  }

  &-container {
    max-height: old-variables.$max-height-navigation-menu-container;
    overflow-x: hidden;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    @include mixins.scrollbar-vertical;
  }

  &-sub-container {
    display: flex;
    flex-direction: column;
  }
}
.row {
  display: grid;
  padding: 4px;
  grid-template-columns: v-bind(gridStyle);
  width: 100%;
  &--first-column-wide {
    grid-template-columns: v-bind(gridStyle);
  }
  .column:not(:first-child) {
    text-align: right;
    width: 55px;
  }
  .column:last-child {
    text-align: right;
    width: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: variables.$color-primary;
  }
  .column--first {
    display: flex;
    text-align: left;
    flex-direction: row-reverse;
    justify-content: flex-end;
    p {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .wrapper-box-circle {
      padding-left: 4px;
      padding-right: 12px;
      display: inline-flex;
      align-items: center;
    }
  }
  &--clickable {
    cursor: pointer;
    .column .arrow-button-icon {
      overflow: unset;
      margin: 0 variables.$margin-xsmall;
      transform: rotate(180deg);

      &--object-expanded {
        transform: rotate(0);
      }
    }
    &:hover {
      background-color: rgba(variables.$color-primary-light, 0.2);
    }
  }
}
.tooltip {
  filter: drop-shadow(0px 0px 0.6px rgb(0, 0, 0));
  margin-top: -2px;
  :deep(.tooltip__arrow) {
    filter: unset;
  }
}
</style>
